import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fullApplicantName } from './formFields';
import type { LicenseOrderDetail } from './types';

const PLACEHOLDER_DNI = '00000000T';

export type ApplicantFormState = {
  firstName: string;
  lastName: string;
  secondLastName: string;
  dni: string;
  birthDate: string;
  idesp: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  province: string;
  country: string;
  contactEmail: string;
  contactPhone: string;
};

function blankPlaceholder(value: string): string {
  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
  if (
    !normalized ||
    normalized === 'pendiente' ||
    normalized === 'pendiente de datos' ||
    normalized === 'revision' ||
    normalized.startsWith('pendiente ')
  ) {
    return '';
  }
  return value.trim();
}

function seedForm(detail: LicenseOrderDetail): ApplicantFormState {
  const applicant = detail.applicant;
  const dni = String(applicant?.dni || '').trim().toUpperCase();
  const birth = String(applicant?.birthDate || '').slice(0, 10);
  const postal = String(applicant?.postalCode || '').trim();
  return {
    firstName: blankPlaceholder(String(applicant?.firstName || '')),
    lastName: blankPlaceholder(String(applicant?.lastName || '')),
    secondLastName: String(applicant?.secondLastName || '').trim(),
    dni: dni === PLACEHOLDER_DNI ? '' : dni,
    birthDate: birth === '1900-01-01' ? '' : birth,
    idesp: String(applicant?.idesp || detail.recipeExtras?.idesp || '').trim(),
    addressLine1: blankPlaceholder(String(applicant?.addressLine1 || '')),
    addressLine2: String(applicant?.addressLine2 || '').trim(),
    postalCode: postal === '00000' || postal === '0000' ? '' : postal,
    city: blankPlaceholder(String(applicant?.city || '')),
    province: blankPlaceholder(String(applicant?.province || '')),
    country: String(applicant?.country || 'ES').trim() || 'ES',
    contactEmail: String(detail.contactEmail || '').trim(),
    contactPhone: String(detail.contactPhone || '').trim(),
  };
}

function displayValue(value: string | null | undefined): string {
  const text = blankPlaceholder(String(value || ''));
  if (!text || text.toUpperCase() === PLACEHOLDER_DNI || text.startsWith('1900-01-01')) return '—';
  if (text === '00000' || text === '0000') return '—';
  return text;
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] leading-tight text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground select-all break-words leading-snug">
        {displayValue(value)}
      </div>
    </div>
  );
}

function InputField({
  label,
  value,
  onChange,
  type = 'text',
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  className?: string;
}) {
  return (
    <label className={className}>
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
      />
    </label>
  );
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function AccountBadge({ detail }: { detail: LicenseOrderDetail }) {
  const account = detail.account;
  if (!account) return null;

  const tone = !account.registered
    ? 'border-rose-500/25 bg-rose-500/10 text-rose-500'
    : account.timing === 'before'
      ? 'border-blue-400/25 bg-blue-400/10 text-blue-400'
      : 'border-emerald-500/25 bg-emerald-500/10 text-emerald-500';

  const label = !account.registered
    ? 'No está registrado en AiPeces'
    : account.timing === 'same_day'
      ? 'Se registró en AiPeces el mismo día'
      : account.timing === 'after'
        ? 'Se registró en AiPeces después del pedido'
        : account.timing === 'before'
          ? 'Registrado en AiPeces antes del pedido'
          : 'Registrado en AiPeces';

  const when = account.registeredAt ? fmtDate(account.registeredAt) : '';

  return (
    <div className={cn('rounded-lg border px-3 py-2 text-sm', tone)}>
      <div className="font-medium">{label}</div>
      {(account.displayName || when || account.userId) && (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {account.displayName && <span>{account.displayName}</span>}
          {when && <span>Alta {when}</span>}
          {account.userId && (
            <Link
              to={`/users/${account.userId}`}
              className="font-medium underline underline-offset-2"
              onClick={(e) => e.stopPropagation()}
            >
              Ver ficha
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

interface Props {
  detail: LicenseOrderDetail;
  pending: boolean;
  onSave: (form: ApplicantFormState) => Promise<void>;
}

export function LicenseApplicantSection({ detail, pending, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => seedForm(detail));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setError(null);
    setForm(seedForm(detail));
  }, [detail.id]);

  const applicant = detail.applicant;
  const dniMissing = !applicant?.dni || applicant.dni === PLACEHOLDER_DNI;
  const set = (key: keyof ApplicantFormState) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <section>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
          Solicitante
        </h3>
        {!editing && (
          <button
            type="button"
            onClick={() => {
              setForm(seedForm(detail));
              setError(null);
              setEditing(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary/15"
          >
            <Pencil size={12} />
            Editar datos
          </button>
        )}
      </div>

      {detail.account && (
        <div className="mb-3">
          <AccountBadge detail={detail} />
        </div>
      )}

      {editing ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <InputField label="Nombre" value={form.firstName} onChange={set('firstName')} />
          <InputField label="Primer apellido" value={form.lastName} onChange={set('lastName')} />
          <InputField
            label="Segundo apellido"
            value={form.secondLastName}
            onChange={set('secondLastName')}
          />
          <InputField label="DNI / NIE / Pasaporte" value={form.dni} onChange={set('dni')} />
          <InputField
            label="Fecha de nacimiento"
            type="date"
            value={form.birthDate}
            onChange={set('birthDate')}
          />
          <InputField label="IDESP / IXESP" value={form.idesp} onChange={set('idesp')} />
          <p className="col-span-2 -mt-2 text-[11px] leading-snug text-muted-foreground">
            El IDESP/IXESP solo corresponde a documentos españoles. Déjalo vacío para pasaportes
            o documentos extranjeros.
          </p>
          <InputField
            label="Email"
            type="email"
            value={form.contactEmail}
            onChange={set('contactEmail')}
          />
          <InputField label="Teléfono" value={form.contactPhone} onChange={set('contactPhone')} />
          <InputField
            className="col-span-2"
            label="Dirección"
            value={form.addressLine1}
            onChange={set('addressLine1')}
          />
          <InputField label="Piso / extra" value={form.addressLine2} onChange={set('addressLine2')} />
          <InputField label="Código postal" value={form.postalCode} onChange={set('postalCode')} />
          <InputField label="Localidad" value={form.city} onChange={set('city')} />
          <InputField label="Provincia" value={form.province} onChange={set('province')} />
          <InputField label="País" value={form.country} onChange={set('country')} />
          <p className="col-span-2 text-[11px] leading-snug text-muted-foreground">
            Al guardar, el pedido y la factura (si ya está emitida) usan este nombre, documento y
            dirección. El Excel de operaciones los lee de la factura.
          </p>
          {error && <p className="col-span-2 text-sm text-rose-500">{error}</p>}
          <div className="col-span-2 flex gap-2">
            <button
              type="button"
              disabled={pending}
              onClick={() => {
                void (async () => {
                  setError(null);
                  try {
                    await onSave(form);
                    setEditing(false);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'No se pudieron guardar los datos');
                  }
                })();
              }}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              {pending ? <Loader2 size={14} className="animate-spin" /> : null}
              Guardar
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setError(null);
                setForm(seedForm(detail));
              }}
              className="px-3 py-2 rounded-lg bg-muted text-sm"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-3">
            <div className="col-span-2">
              <Field label="Nombre y apellidos" value={fullApplicantName(applicant || {})} />
            </div>
            <Field label="DNI / NIE / Pasaporte" value={applicant?.dni} />
            <Field label="IDESP / IXESP" value={applicant?.idesp || detail.recipeExtras?.idesp} />
            <Field label="Fecha de nacimiento" value={applicant?.birthDate} />
            <Field label="Email" value={detail.contactEmail} />
            <Field label="Teléfono" value={detail.contactPhone} />
          </div>
          {dniMissing && (
            <p className="text-xs text-amber-600">
              Falta el documento del titular. Pulsa Editar datos para guardar nombre, documento y dirección.
            </p>
          )}
          <div>
            <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Dirección
            </h3>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-3">
              <div className="col-span-2">
                <Field label="Dirección" value={applicant?.addressLine1} />
              </div>
              <Field label="Piso / extra" value={applicant?.addressLine2} />
              <Field label="Código postal" value={applicant?.postalCode} />
              <Field label="Localidad" value={applicant?.city} />
              <Field label="Provincia" value={applicant?.province} />
              <Field label="País" value={applicant?.country} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
